import api from '@/shared/api/client';

// La recherche du point de livraison, demandee AU SERVEUR, et ecrite ICI seulement.
//
// Les deux parcours -- l'installateur sur le terrain et l'auto-inscription par QR --
// posent la meme question : quel est le point de livraison de ce logement ? Deux copies
// de cet appel divergeraient au premier correctif, et la plus permissive deviendrait
// celle qui propose un faux numero. Le chemin de la route n'est donc ecrit qu'a un seul
// endroit, et un test le verifie.
//
// LA REGLE, ET ELLE VAUT AUSSI COTE ECRAN
//
//     Un PDL FAUX est pire qu'un PDL ABSENT.
//
// Quand le serveur ne trouve pas, il ne rend AUCUN numero, et l'ecran ne remplit rien.
// Le champ reste vide et saisissable : une recherche qui echoue ne doit jamais empecher
// de saisir.
//
// LA REPONSE EST TOUJOURS UN 200, MEME QUAND ELLE NE TROUVE RIEN
//
// « Aucun point de livraison ne correspond » est une reponse, pas une panne. L'ancienne
// version rendait 404 dans ce cas, ce qui obligeait chaque ecran a traiter une absence
// comme une erreur — et a inventer sa propre phrase. Le verdict rend :
//
//     trouve      vrai seulement si un numero est propose
//     pdl         le numero, ou null. JAMAIS un numero fabrique.
//     source      d'ou il vient ('energrid'), a montrer a l'utilisateur
//     motif       aucun_resultat · commune_indeterminee · adresse_incomplete ·
//                 resultat_ambigu · recherche_indisponible · service_en_erreur
//     message     la phrase a afficher, redigee UNE FOIS cote serveur
//     commune     la commune retenue, pour que le poseur repere une adresse mal comprise
//     fiche       ce qu'ENEDIS sait : titulaire, adresse, etat et type du compteur
//     rapprochement  le verdict champ par champ, et la valeur a ecrire quand elle differe
//
// DEUX ROUTES, UN SEUL SERVICE, ET C'EST VOULU
//
// Le terrain et le QR passent par la route PUBLIQUE, plafonnee par adresse : elle est
// ouverte a tout internet, et chaque appel part chez Energrid avec notre cle. Le CRM
// passe par une route AUTHENTIFIEE, plafonnee par compte -- sinon tout un bureau
// derriere une seule sortie internet partagerait le quota prevu contre un balayage
// anonyme, et le quatrieme client saisi se ferait refuser.
//
// Les deux appellent `recherche_pdl.chercher` cote serveur. Les verdicts et les phrases
// sont donc les memes, par construction.
const ROUTE_PUBLIQUE = '/onboarding/search-pdl';
const ROUTE_AUTHENTIFIEE = '/clients/search-pdl';

export async function rechercherPdl({ nom, prenom, adresse, complementAdresse,
                                      codePostal, ville }, { authentifie = false } = {}) {
  const { data } = await api.get(authentifie ? ROUTE_AUTHENTIFIEE : ROUTE_PUBLIQUE, {
    params: {
      nom, adresse, code_postal: codePostal, ville,
      // Ni le prenom ni le complement ne servent A CHERCHER -- Energrid ne prend que le
      // nom de famille et la voie. Ils servent a RAPPROCHER ce qu'il rend de ce qui a
      // ete saisi, et c'est la que le gain est reel : une faute sur le prenom passe la
      // recherche sans etre vue, une faute sur le nom ne rend simplement aucun resultat.
      prenom: prenom || '',
      complement_adresse: complementAdresse || '',
    },
  });
  return data;
}

// Vrai quand les quatre champs qu'Energrid exige sont saisis. Sert a ne pas partir pour
// rien : sans l'un d'eux, la recherche geographique rend un 400 qui les reclame tous.
export function peutChercher({ nom, adresse, codePostal, ville }) {
  return Boolean(
    String(nom || '').trim()
    && String(adresse || '').trim()
    && String(codePostal || '').trim()
    && String(ville || '').trim(),
  );
}

// Ce que l'ecran affiche quand le serveur n'a pas repondu du tout (reseau coupe sur un
// chantier, serveur redemarre). On ne reutilise pas le message d'un verdict : il n'y en
// a pas, et en inventer un qui ressemble a « aucun resultat » ferait croire que la
// recherche a abouti.
export const MESSAGE_INJOIGNABLE =
  "La recherche n'a pas pu etre lancee. Saisissez le numero a la main.";

// Le plafond de la route publique mord au-dela de 20 appels par tranche de cinq
// minutes et par adresse. Le dire clairement evite que le poseur reclique quinze fois.
export const MESSAGE_TROP_D_APPELS =
  'Trop de recherches en peu de temps. Patientez quelques minutes, ou saisissez le '
  + 'numero a la main.';

// ---------------------------------------------------------------------------
// LE SENS RETOUR : un numero saisi, et ce qu'Enedis en sait qui revient
// ---------------------------------------------------------------------------
//
// Meme discipline que ci-dessus, meme raison : le chemin des routes n'est ecrit qu'a un
// seul endroit, et un test le verifie. Deux copies divergeraient au premier correctif.
//
// ⚠️ CE N'EST PAS UNE LECTURE, ET C'EST LA SEULE CHOSE A RETENIR AVANT DE L'APPELER.
//
// Chaque appel peut poser chez Energrid une DECLARATION DE CONSENTEMENT portant le nom
// et l'adresse electronique d'une personne reelle. La revocation repond 404 tant que
// l'accord n'est pas donne : on ne la reprend pas. D'ou `consentementAccepte`
// obligatoire, un plafond serre cote serveur, et un bouton qui ne part jamais tout seul.
//
// CE QUE LE SERVEUR REND, ET CE QU'IL NE REND PAS
//
//     trouve         vrai des qu'Enedis a repondu quelque chose du compteur
//     motif          pdl_mal_forme · pdl_inconnu · pdl_sans_donnees ·
//                    consentement_manquant · consentement_refuse ·
//                    consentement_en_attente · titulaire_incomplet ·
//                    recherche_indisponible · service_en_erreur
//     message        la phrase a afficher, redigee UNE FOIS cote serveur
//     fiche          ce qu'Enedis sait du compteur
//     titulaire      { verdict: confirme | autre_titulaire | non_confirme,
//                      nom_enedis, reference_trouvee }
//     rapprochement  le verdict champ par champ, VIDE tant que le titulaire n'est pas
//                    confirme
//     consentement   { etat, reutilise, declaration_simulee }
//
// ⚠️ L'ADRESSE REVIENT MEME SANS NOM CONFIRME — arbitrage de Boubacar du 07/09 : « si le
// PDL saisi donne l'adresse c'est deja suffisant, meme si on n'a pas le nom, au moins les
// adresses bien remplies ». Le `message` dit alors explicitement que le nom n'a pas ete
// confirme, et l'ecran doit l'afficher : sans lui, une adresse NON VERIFIEE passerait
// pour une adresse verifiee.
//
// ⚠️ UNE SEULE EXCEPTION, ET ELLE EST FERMEE SUR LES TROIS CANAUX : `titulaire.verdict
// === 'autre_titulaire'`. La le nom saisi existe bien a cette adresse, mais sur un AUTRE
// compteur -- le numero recopie est celui du voisin. Le serveur ne rend alors ni fiche
// nominative ni rapprochement, et l'ecran n'a rien a ecrire. « On ne sait pas » et « on
// sait que c'est faux » n'appellent pas le meme geste.
const ROUTE_RETOUR_PUBLIQUE = '/onboarding/retrouver-pdl';
const ROUTE_RETOUR_AUTHENTIFIEE = '/clients/retrouver-pdl';

export async function retrouverParPdl({ pdl, nom, prenom, email, adresse,
                                        complementAdresse, codePostal, ville,
                                        clientId },
                                      { authentifie = false } = {}) {
  const { data } = await api.post(
    authentifie ? ROUTE_RETOUR_AUTHENTIFIEE : ROUTE_RETOUR_PUBLIQUE, {
      pdl,
      nom,
      prenom,
      email,
      code_postal: codePostal || '',
      adresse: adresse || '',
      ville: ville || '',
      complement_adresse: complementAdresse || '',
      client_id: clientId || null,
      // Jamais une valeur par defaut : c'est la case cochee a l'ecran qui la porte.
      consentement_accepte: true,
    });
  return data;
}

// Vrai quand le serveur a de quoi partir. Le numero doit avoir la forme qu'ENEDIS
// publie -- `(0|1|2|3|4|5|9)` puis 13 chiffres -- et pas seulement quatorze chiffres :
// les premiers chiffres 6, 7 et 8 sont refuses par construction, et partir avec eux
// consommerait un appel chez un tiers pour un refus certain.
export const FORME_PDL_ENEDIS = /^(0|1|2|3|4|5|9)\d{13}$/;

// ⚠️ LE NUMERO SEUL SUFFIT DEPUIS LE 09/09/2026, ET C EST UNE MESURE QUI L A PERMIS.
//
// Ce controle exigeait cinq champs : numero, nom, prenom, adresse electronique et code
// postal. Ils n etaient pas ceux de la DONNEE, mais ceux du CONSENTEMENT, que ce chemin
// posait avant de lire. Mesure du 09/09 sur un compteur vierge de tout consentement :
//
//     POST /recette/requete { grd, pdl.reference, donnees }  ->  200
//          43 RTE DE RUAUDIN · 72230 ARNAGE · Linky · En service
//
// Le nom, quand il est la, sert desormais a CONFIRMER que le compteur est bien celui du
// client. Il n autorise plus rien.
export function peutRetrouver({ pdl }) {
  return FORME_PDL_ENEDIS.test(String(pdl || '').trim());
}

// Ce que l'ecran dit quand le serveur n'a pas repondu du tout. On ne reutilise pas le
// message d'un verdict : il n'y en a pas, et en inventer un qui ressemble a « numero
// inconnu » ferait croire que le compteur a ete interroge.
export const MESSAGE_VERIFICATION_INJOIGNABLE =
  "La verification n'a pas pu etre lancee. Saisissez les informations a la main.";

export const MESSAGE_TROP_DE_VERIFICATIONS =
  'Trop de verifications en peu de temps. Patientez quelques minutes, ou saisissez les '
  + 'informations a la main.';
